import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api } from '../services/api'
import { resolveClient, type ClientConfig } from '../config/clients'
import type { SupplierRef } from '../types'

/**
 * Contexto de trabajo del portal:
 * - El **cliente** (receptor de los documentos) viene dado por la ruta del sitio y
 *   define el header `x-ax-workspace`.
 * - El **proveedor** se resuelve tras el login consultando los suppliers que el usuario
 *   puede operar para ese cliente, y define el header `x-ax-supplier`
 *   (formato `COUNTRY#TAX_ID`, idéntico a la clave del claim `com.axteroid.suppliers`).
 */
const STORAGE_PREFIX = 'fax-sup-supplier:'

function loadStoredSupplier(accId: string): SupplierRef | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + accId)
    return raw ? (JSON.parse(raw) as SupplierRef) : null
  } catch {
    return null
  }
}

function saveStoredSupplier(accId: string, supplier: SupplierRef | null): void {
  if (supplier) {
    localStorage.setItem(STORAGE_PREFIX + accId, JSON.stringify(supplier))
  } else {
    localStorage.removeItem(STORAGE_PREFIX + accId)
  }
}

/**
 * Aterrizaje de una invitación aceptada: deja pre-seleccionado el proveedor invitado para el
 * cliente (mismo storage que la selección manual; al cargar los proveedores del usuario, la
 * restauración lo elige si está entre sus accesos).
 */
export function storeInvitedSupplier(accId: string, country: string, taxId: string): void {
  saveStoredSupplier(accId, { country, tax_id: taxId, name: '' })
}

/** Último cliente usado: permite entrar por la raíz del sitio y recuperar su ruta. */
const LAST_CLIENT_KEY = 'fax-sup-last-client'

/**
 * Último cliente usado, solo si sigue habilitado en este ambiente: el mapeo depende de la
 * plataforma y el stage, así que un cliente guardado puede ya no existir en este build.
 */
export function loadLastClientKey(): string | null {
  const key = localStorage.getItem(LAST_CLIENT_KEY)
  return key && resolveClient(key) ? key : null
}

export const useSessionStore = defineStore('session', () => {
  const clientKey = ref<string | null>(null)
  const client = ref<ClientConfig | null>(null)
  const organizationName = ref<string | null>(null)
  const suppliers = ref<SupplierRef[]>([])
  const suppliersLoaded = ref(false)
  const selectedSupplier = ref<SupplierRef | null>(null)

  const accId = computed(() => client.value?.accId ?? null)
  const clientName = computed(() => organizationName.value ?? client.value?.accId ?? '')
  const supplierHeader = computed(() =>
    selectedSupplier.value ? `${selectedSupplier.value.country}#${selectedSupplier.value.tax_id}` : null,
  )
  /** El usuario es OWNER del proveedor seleccionado (habilita la gestión de usuarios). */
  const isOwner = computed(() => selectedSupplier.value?.role === 'OWNER')

  /**
   * Resuelve el nombre a mostrar del cliente desde Keycloak (endpoint público por alias:
   * `GET /custom-resources/organizations/{acc_id}`, sin token — un Bearer solo provocaría un
   * preflight CORS innecesario). Mientras carga —o si falla— clientName cae al accId.
   */
  async function loadOrganizationName(targetAccId: string): Promise<void> {
    const realm = import.meta.env.VITE_OIDC_REALM
    try {
      const org = await api.getPublic<{ name: string; trade_name?: string }>(
        `/login/realms/${realm}/custom-resources/organizations/${targetAccId}`,
      )
      // El cliente pudo cambiar mientras respondía: solo aplica si sigue siendo el mismo.
      if (client.value?.accId === targetAccId) {
        organizationName.value = org.trade_name?.trim() || org.name
      }
    } catch (err) {
      console.error(`No se pudo resolver la organización ${targetAccId}:`, err)
    }
  }

  function setClient(key: string, config: ClientConfig): void {
    localStorage.setItem(LAST_CLIENT_KEY, key)
    if (clientKey.value === key) return
    clientKey.value = key
    client.value = config
    organizationName.value = null
    suppliers.value = []
    suppliersLoaded.value = false
    selectedSupplier.value = null
    void loadOrganizationName(config.accId)
  }

  async function loadSuppliers(): Promise<void> {
    if (!client.value) return
    const realm = import.meta.env.VITE_OIDC_REALM
    const data = await api.get<SupplierRef[]>(
      `/login/realms/${realm}/custom-resources/suppliers/organizations/${client.value.accId}/suppliers`,
    )
    suppliers.value = data
    suppliersLoaded.value = true

    if (data.length === 1) {
      selectSupplier(data[0])
      return
    }

    // Restaurar la selección previa solo si sigue vigente entre los grants del usuario.
    const stored = loadStoredSupplier(client.value.accId)
    const match = stored
      ? data.find((s) => s.country === stored.country && s.tax_id === stored.tax_id)
      : null
    if (match) {
      selectedSupplier.value = match
    } else if (stored) {
      saveStoredSupplier(client.value.accId, null)
    }
  }

  function selectSupplier(supplier: SupplierRef): void {
    selectedSupplier.value = supplier
    if (client.value) {
      saveStoredSupplier(client.value.accId, supplier)
    }
  }

  function clear(): void {
    if (client.value) {
      saveStoredSupplier(client.value.accId, null)
    }
    suppliers.value = []
    suppliersLoaded.value = false
    selectedSupplier.value = null
  }

  return {
    clientKey,
    client,
    suppliers,
    suppliersLoaded,
    selectedSupplier,
    accId,
    clientName,
    supplierHeader,
    isOwner,
    setClient,
    loadSuppliers,
    selectSupplier,
    clear,
  }
})
